import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import {
  FD_WRITE_TOKEN_KEY,
  setPreferredWriteScope,
  clearAuthCookies,
} from '../utils/writeAuth';

// Owns the app's two write scopes — admin (content editing, save) and
// front-desk (the main-app gate). Both tokens live in HttpOnly cookies;
// this hook holds only the in-memory flags + cooldowns + the handful of
// effects that keep everything in sync:
//
//   * Boot effect that calls /api/auth/whoami so we know whether the
//     httpOnly cookie is still valid before any downstream effect fires
//     auth-gated requests. The cookie is the single source of truth —
//     no parallel localStorage marker (which used to outlive the 12h
//     JWT and cause a 401 cascade + racing logout-vs-login on reload).
//   * `modality:auth-401` listener that boots the FD gate back on-screen
//     whenever any authFetch comes back 401 (admin-mode sessions are
//     left alone — they drop back to public read-only via a separate
//     flow).
//   * `loginVisible` auto-collapse timer: the floating 🔑 admin prompt
//     hides after 10s of inactivity.
//   * Global click/keydown blocker active while FD auth is false.
//
// Downstream hooks (useSupport, useFdLiveChat, useAppData) read
// `adminMode`, `frontdeskAuth`, and `writeAuthEpoch` — the epoch counter
// bumps every time a JWT is newly stored so those hooks' fetches re-run.
export function useAuth({ playClickSound, onLogout }) {
  const [adminMode, setAdminMode] = useState(false);
  const [frontdeskAuth, setFrontdeskAuth] = useState(false);
  // True once whoami has resolved (success or 401). Downstream effects
  // (useSupport, etc.) gate on this so they don't fire a 401 burst while
  // we're still figuring out whether the user has a valid cookie. That
  // burst is what triggered the racing /api/logout calls that clobbered
  // freshly-set login cookies.
  const [authHydrated, setAuthHydrated] = useState(false);
  const [password, setPassword] = useState('');
  const [fdPassword, setFdPassword] = useState('');
  const [adminLoginError, setAdminLoginError] = useState('');
  const [fdLoginError, setFdLoginError] = useState('');

  // Login cooldown + attempt counters. adminAttempts is updated inside
  // the functional setState so we don't race on rapid repeats; the
  // exposed setter is kept to match the pre-refactor surface.
  // Attempt counters use setState-functional form inside login handlers;
  // the current value isn't read outside those callbacks, so we only
  // keep the setter. Cooldown values ARE read (to build the "wait Ns"
  // message), so both ends are kept.
  const [, setAdminAttempts] = useState(0);
  const [adminCooldown, setAdminCooldown] = useState(0);
  const [fdAttempts, setFdAttempts] = useState(0);
  const [fdCooldown, setFdCooldown] = useState(0);

  const [loginVisible, setLoginVisible] = useState(false);

  // Bumps when a write JWT is stored so dependent fetches (support,
  // employees, tickets) re-run without waiting for their own
  // adminMode/frontdeskAuth dep to flip.
  const [writeAuthEpoch, setWriteAuthEpoch] = useState(0);

  const inputRef = useRef(null);
  const loginTimeoutRef = useRef(null);

  // Ask the server whether our httpOnly cookie is still valid. The cookie
  // is the only source of truth — we no longer keep a parallel
  // localStorage marker (which used to outlive the 12h JWT and cause a
  // 401 cascade on page load).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/auth/whoami', { credentials: 'include' });
        if (!alive) return;
        if (res.ok) {
          const j = await res.json().catch(() => null);
          if (j && j.ok && j.scope === 'frontdesk') {
            setPreferredWriteScope('frontdesk');
            setFrontdeskAuth(true);
            setWriteAuthEpoch((e) => e + 1);
          }
        }
      } catch {
        // network failure: leave unauthed; login gate will appear
      } finally {
        if (alive) setAuthHydrated(true);
      }
    })();
    // Clean up stale legacy keys from older builds.
    try { localStorage.removeItem('frontdeskAuth'); } catch { /* ignore */ }
    try { localStorage.removeItem('modalityFdWriteTokenPersisted'); } catch { /* ignore */ }
    return () => { alive = false; };
  }, []);

  // 401 recovery: any authenticated request that comes back 401
  // (expired/missing token) forces the FD gate back on-screen. Skipped
  // during adminMode so an admin's short-lived session doesn't kick
  // them out of content editing.
  useEffect(() => {
    const on401 = () => {
      if (adminMode) return;
      // Do NOT call /api/logout here. A 401 already proves the cookie is
      // invalid server-side; firing a logout request in parallel raced
      // with the user's next login and could clobber the fresh cookie
      // with an expired one.
      setFdPassword('');
      try {
        sessionStorage.removeItem(FD_WRITE_TOKEN_KEY);
      } catch { /* ignore */ }
      try {
        setPreferredWriteScope(null);
      } catch { /* ignore */ }
      setFrontdeskAuth(false);
      setWriteAuthEpoch((e) => e + 1);
      setPassword('');
      setLoginVisible(true);
      try {
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch { /* ignore */ }
    };
    window.addEventListener('modality:auth-401', on401);
    return () => window.removeEventListener('modality:auth-401', on401);
  }, [adminMode]);

  // Floating admin 🔑 prompt auto-collapses after 10s of inactivity.
  useEffect(() => {
    if (loginVisible) {
      if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
      loginTimeoutRef.current = setTimeout(() => {
        setLoginVisible(false);
        setPassword('');
      }, 10000);
    } else if (loginTimeoutRef.current) {
      clearTimeout(loginTimeoutRef.current);
    }
    return () => {
      if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
    };
  }, [loginVisible]);

  // While FD auth is false, swallow all click/key input so unauthorized
  // users can't interact with the app behind the login modal. Elements
  // inside the login form opt out via data-allow-interaction.
  useEffect(() => {
    const blocker = (e) => {
      if (frontdeskAuth) return;
      try {
        if (e.target && e.target.closest && e.target.closest('[data-allow-interaction]')) return;
      } catch (err) {
        // ignore
      }
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    const keyBlocker = (e) => {
      if (frontdeskAuth) return;
      try {
        if (e.target && e.target.closest && e.target.closest('[data-allow-interaction]')) return;
      } catch (err) { /* ignore */ }
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    const events = ['click', 'mousedown', 'mouseup', 'touchstart', 'touchend'];
    events.forEach((ev) => window.addEventListener(ev, blocker, true));
    window.addEventListener('keydown', keyBlocker, true);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, blocker, true));
      window.removeEventListener('keydown', keyBlocker, true);
    };
  }, [frontdeskAuth]);

  const handleLogin = async () => {
    playClickSound();

    if (Date.now() < adminCooldown) {
      setAdminLoginError(`Too many attempts. Please wait ${Math.ceil((adminCooldown - Date.now()) / 1000)}s.`);
      return;
    }

    setAdminLoginError('');
    try {
      const res = await fetch('/api/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setPreferredWriteScope('admin');
        setWriteAuthEpoch((e) => e + 1);
        setAdminMode(true);
        setLoginVisible(false);
        setPassword('');
        setAdminLoginError('');
        if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
        setAdminAttempts(0);
        setAdminCooldown(0);
      } else {
        setAdminAttempts((prev) => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            setAdminCooldown(Date.now() + 30000);
            setAdminLoginError('Maximum attempts reached. Please wait 30 seconds.');
            return 0;
          }
          setAdminLoginError(json.message || 'Wrong password');
          return newCount;
        });
      }
    } catch (err) {
      console.error('Login error', err);
      setAdminLoginError('Login error. Please try again.');
    }
  };

  const handleFrontdeskLogin = async (pwd, remember = true) => {
    playClickSound();

    if (Date.now() < fdCooldown) {
      return { success: false, message: `Too many attempts. Please wait ${Math.ceil((fdCooldown - Date.now()) / 1000)}s.` };
    }

    try {
      const res = await fetch('/api/frontdesk-login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd, remember }),
      });
      const text = await res.text();
      let json = null;
      try { json = JSON.parse(text); } catch (e) { /* fallback for text responses */ }
      if (res.ok && (json ? json.success : text.includes('success') || text.includes('Default'))) {
        setPreferredWriteScope('frontdesk');
        setWriteAuthEpoch((e) => e + 1);
        setFrontdeskAuth(true);
        // Cookie (Max-Age=2592000 when remember) handles persistence;
        // no parallel localStorage marker — that drift caused the
        // 401-on-stale-marker race.
        setFdAttempts(0);
        setFdCooldown(0);
        return { success: true };
      }

      const message = json?.message || text || 'Invalid password';

      setFdAttempts((prev) => {
        const newCount = prev + 1;
        if (newCount >= 5) {
          setFdCooldown(Date.now() + 30000);
          return 0;
        }
        return newCount;
      });

      // fdAttempts here reflects the value BEFORE the setState above —
      // the inline setter runs async, so predicting "5th attempt" from
      // the outer closure's view means checking >= 4.
      if (fdAttempts >= 4) {
        return { success: false, message: 'Maximum attempts reached. Please wait 30 seconds.' };
      }

      return { success: false, message };
    } catch (err) {
      console.error('Frontdesk login error', err);
      return { success: false, message: err.message || 'Login error' };
    }
  };

  const handleLogout = () => {
    playClickSound();
    setAdminMode(false);
    setPassword('');
    if (onLogout) onLogout();
    clearAuthCookies('admin');
    setPreferredWriteScope(frontdeskAuth ? 'frontdesk' : null);
    setWriteAuthEpoch((e) => e + 1);
  };

  const handleSetAdminPassword = async (newPassword) => {
    try {
      const res = await authFetch('/api/admin-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });
      return await res.json();
    } catch (err) {
      console.error('Set admin password error', err);
      throw err;
    }
  };

  // adminAttempts / setAdminAttempts / fdAttempts / setFdAttempts /
  // adminCooldown / fdCooldown are deliberately not returned —
  // cooldown + attempt counters are purely hook-internal. They gate
  // retries inside handleLogin / handleFrontdeskLogin and no consumer
  // reads them.
  return {
    adminMode, setAdminMode,
    frontdeskAuth, setFrontdeskAuth,
    password, setPassword,
    fdPassword, setFdPassword,
    adminLoginError, setAdminLoginError,
    fdLoginError, setFdLoginError,
    loginVisible, setLoginVisible,
    writeAuthEpoch,
    authHydrated,
    inputRef, loginTimeoutRef,
    handleLogin,
    handleFrontdeskLogin,
    handleLogout,
    handleSetAdminPassword,
  };
}
